import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Grid,
    Checkbox,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import dayjs from 'dayjs';
import { pricingRuleAPI } from '../services/api';
import { toast } from 'react-toastify';
import { format24To12Hour, format12To24Hour, isTimeRangeWrappingMidnight, formatTimeRange, validatePricingRuleOverlap } from '../utils/timeUtils';

const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

const emptyRule = {
    // Hidden defaults for backend-required fields
    name: 'Rule',
    active: true,
    priority: 100,
    appliesToAllCourts: true,
    // Visible/simple fields
    recurrence: 'WEEKLY',
    daysOfWeekCsv: '1,2,3,4,5,6,7',
    startDate: '',
    endDate: '',
    specificDatesCsv: '',
    startTime: '12:00 AM',
    endTime: '11:00 PM',
    wrapsMidnight: false,
    fixedAmount: 0
};

const PricingRulesPage = () => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [selectedDays, setSelectedDays] = useState([1,2,3,4,5,6,7]);
    const [validationResult, setValidationResult] = useState({ hasOverlap: false, overlappingRules: [], message: '' });

    const dayOptions = [
        { v: 1, label: 'Mon' },
        { v: 2, label: 'Tue' },
        { v: 3, label: 'Wed' },
        { v: 4, label: 'Thu' },
        { v: 5, label: 'Fri' },
        { v: 6, label: 'Sat' },
        { v: 7, label: 'Sun' },
    ];

    const loadRules = async () => {
        setLoading(true);
        try {
            const res = await pricingRuleAPI.list();
            setRules(res.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRules();
    }, []);

    const openCreate = () => {
        setEditingRule({ ...emptyRule });
        setSelectedDays([1,2,3,4,5,6,7]);
        setValidationResult({ hasOverlap: false, overlappingRules: [], message: '' });
        setEditorOpen(true);
    };

    const openEdit = (rule) => {
        // Convert 24-hour backend format to 12-hour display format
        const startTime12 = format24To12Hour(rule.startTime || '00:00');
        const endTime12 = format24To12Hour(rule.endTime || '23:00');
        
        setEditingRule({ ...rule, startTime: startTime12, endTime: endTime12 });
        const days = (rule.daysOfWeekCsv || '')
            .split(',')
            .map(s => parseInt(s.trim(), 10))
            .filter(n => !isNaN(n));
        setSelectedDays(days.length ? days : [1,2,3,4,5,6,7]);
        setValidationResult({ hasOverlap: false, overlappingRules: [], message: '' });
        setEditorOpen(true);
    };

    const closeEditor = () => {
        setEditorOpen(false);
        setEditingRule(null);
        setValidationResult({ hasOverlap: false, overlappingRules: [], message: '' });
    };

    // Validate current rule against existing rules
    const validateCurrentRule = (rule, days) => {
        if (!rule || !rule.startTime || !rule.endTime) {
            setValidationResult({ hasOverlap: false, overlappingRules: [], message: '' });
            return;
        }
        
        const result = validatePricingRuleOverlap(rule, rules, days);
        setValidationResult(result);
    };

    const handleTimeChange = (field, value) => {
        const newRule = { ...editingRule, [field]: value };
        
        // Auto-detect midnight wrapping
        const start24 = format12To24Hour(newRule.startTime);
        const end24 = format12To24Hour(newRule.endTime);
        const wraps = isTimeRangeWrappingMidnight(start24, end24);
        
        setEditingRule({ ...newRule, wrapsMidnight: wraps });
        
        // Validate after updating
        setTimeout(() => validateCurrentRule(newRule, selectedDays), 100);
    };

    const saveRule = async () => {
        const payload = { ...editingRule };
        // Ensure required hidden defaults
        if (!payload.name) payload.name = 'Rule';
        if (payload.active === undefined) payload.active = true;
        if (payload.priority === undefined || payload.priority === null) payload.priority = 100;
        if (payload.appliesToAllCourts === undefined) payload.appliesToAllCourts = true;
        // Force weekly recurrence and set selected days from checkboxes
        payload.recurrence = 'WEEKLY';
        payload.daysOfWeekCsv = selectedDays.sort((a,b)=>a-b).join(',');
        
        // Convert 12-hour display format to 24-hour backend format
        payload.startTime = format12To24Hour(editingRule.startTime);
        payload.endTime = format12To24Hour(editingRule.endTime);
        if (!payload.appliesToAllCourts && payload.courtIdsCsv) {
            payload.courtIdsCsv = payload.courtIdsCsv
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .join(',');
        }
        // Clear unused fields for simplicity
        payload.startDate = null;
        payload.endDate = null;
        payload.specificDatesCsv = '';

        try {
            if (payload.id) {
                await pricingRuleAPI.update(payload.id, payload);
                toast.success('Rule updated');
            } else {
                await pricingRuleAPI.create(payload);
                toast.success('Rule created');
            }
            closeEditor();
            await loadRules();
        } catch (e) {
            toast.error('Failed to save rule');
        }
    };

    const removeRule = async (id) => {
        await pricingRuleAPI.remove(id);
        loadRules();
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ color: logoColors.brightYellow, fontWeight: 700 }}>Pricing Rules</Typography>
                <Button variant="contained" onClick={openCreate}
                    sx={{ backgroundColor: logoColors.brightYellow, color: logoColors.darkBlue, fontWeight: 700 }}>
                    New Rule
                </Button>
            </Box>

            <Paper elevation={3} sx={{ p: 2, background: `linear-gradient(135deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)` }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: logoColors.brightYellow }}>Days</TableCell>
                            <TableCell sx={{ color: logoColors.brightYellow }}>Time Window</TableCell>
                            <TableCell sx={{ color: logoColors.brightYellow }}>Amount</TableCell>
                            <TableCell sx={{ color: logoColors.brightYellow }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(rules || []).map(rule => (
                            <TableRow key={rule.id}>
                                <TableCell sx={{ color: logoColors.brightYellow }}>
                                    {(rule.daysOfWeekCsv || '')
                                        .split(',')
                                        .map(s=>s.trim())
                                        .filter(Boolean)
                                        .map(n => dayOptions.find(d=>String(d.v)===n)?.label || n)
                                        .join(', ')}
                                </TableCell>
                                <TableCell sx={{ color: logoColors.brightYellow }}>
                                    {formatTimeRange(rule.startTime, rule.endTime)} {rule.wrapsMidnight ? '(wraps)' : ''}
                                </TableCell>
                                <TableCell sx={{ color: logoColors.brightYellow }}>Rs {rule.fixedAmount}</TableCell>
                                <TableCell align="right">
                                    <Button size="small" variant="outlined" sx={{ mr: 1, borderColor: logoColors.brightYellow, color: logoColors.brightYellow }} onClick={() => openEdit(rule)}>Edit</Button>
                                    <Button size="small" variant="outlined" color="error" onClick={() => removeRule(rule.id)}>Delete</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={editorOpen} onClose={closeEditor} maxWidth="md" fullWidth>
                <DialogTitle sx={{ color: logoColors.brightYellow, backgroundColor: logoColors.darkBlue }}>
                    {editingRule?.id ? 'Edit Rule' : 'New Rule'}
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: logoColors.darkBlue }}>
                    {editingRule && (
                        <Box sx={{ mt: 1 }}>
                            {/* Overlap Warning Alert */}
                            {validationResult.hasOverlap && (
                                <Alert severity="warning" sx={{ mb: 2, backgroundColor: '#fff3cd', color: '#856404' }}>
                                    <AlertTitle sx={{ fontWeight: 'bold' }}>{validationResult.message}</AlertTitle>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        The following rules conflict with this time range and days:
                                    </Typography>
                                    <List dense>
                                        {validationResult.overlappingRules.map((rule) => (
                                            <ListItem key={rule.id} sx={{ py: 0.5 }}>
                                                <ListItemText
                                                    primary={`${rule.name} - ${rule.days}`}
                                                    secondary={`${rule.timeRange} (PKR ${rule.fixedAmount})`}
                                                    primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                                                    secondaryTypographyProps={{ fontSize: '0.85rem', color: '#856404' }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Alert>
                            )}
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography sx={{ color: logoColors.brightYellow, mb: 1 }}>Days of Week</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        {dayOptions.map(d => (
                                            <FormControlLabel key={d.v}
                                                control={<Checkbox checked={selectedDays.includes(d.v)}
                                                    onChange={(e)=>{
                                                        const newDays = e.target.checked ? [...new Set([...selectedDays, d.v])] : selectedDays.filter(x=>x!==d.v);
                                                        setSelectedDays(newDays);
                                                        // Validate when days change
                                                        setTimeout(() => validateCurrentRule(editingRule, newDays), 100);
                                                    }}
                                                    sx={{ color: logoColors.brightYellow }} />}
                                                label={<span style={{ color: logoColors.brightYellow }}>{d.label}</span>}
                                            />
                                        ))}
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField 
                                        fullWidth 
                                        label="Start Time (e.g., 6:00 PM)" 
                                        value={editingRule.startTime}
                                        onChange={e => handleTimeChange('startTime', e.target.value)}
                                        placeholder="6:00 PM"
                                        helperText={editingRule.wrapsMidnight ? "Wraps to next day" : ""}
                                        sx={{ 
                                            '& .MuiInputBase-input, & .MuiInputLabel-root': { color: logoColors.brightYellow },
                                            '& .MuiFormHelperText-root': { color: logoColors.lightYellow }
                                        }} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField 
                                        fullWidth 
                                        label="End Time (e.g., 10:00 PM)" 
                                        value={editingRule.endTime}
                                        onChange={e => handleTimeChange('endTime', e.target.value)}
                                        placeholder="10:00 PM"
                                        sx={{ '& .MuiInputBase-input, & .MuiInputLabel-root': { color: logoColors.brightYellow } }} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControlLabel 
                                        control={
                                            <Switch 
                                                checked={editingRule.wrapsMidnight}
                                                onChange={e => setEditingRule({ ...editingRule, wrapsMidnight: e.target.checked })} 
                                            />
                                        }
                                        label={<span style={{ color: logoColors.brightYellow }}>
                                            Wraps Midnight {editingRule.wrapsMidnight ? '✓' : ''}
                                        </span>} 
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField fullWidth label="Fixed Amount (PKR)" type="number" value={editingRule.fixedAmount}
                                               onChange={e => setEditingRule({ ...editingRule, fixedAmount: Number(e.target.value) })}
                                               sx={{ '& .MuiInputBase-input, & .MuiInputLabel-root': { color: logoColors.brightYellow } }} />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ backgroundColor: logoColors.darkBlue }}>
                    <Button onClick={closeEditor} variant="outlined" sx={{ borderColor: logoColors.brightYellow, color: logoColors.brightYellow }}>Cancel</Button>
                    <Button onClick={saveRule} variant="contained" sx={{ backgroundColor: logoColors.brightYellow, color: logoColors.darkBlue, fontWeight: 700 }}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PricingRulesPage;


